import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Bed, Bath, Maximize, Heart, ArrowRight, Star, MapPin } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";

const IMG = {
  agent: "https://images.unsplash.com/photo-1573497019236-17f8177b81e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjYxODl8MHwxfHNlYXJjaHwxfHxyZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjBwb3J0cmFpdCUyMHByb2Zlc3Npb25hbCUyMHdhcm0lMjBmcmllbmRseSUyMHdvbWFufGVufDB8fHx8MTc4ODU2NTY4OXww&ixlib=rb-4.1.0&q=85",
  p1: "https://images.unsplash.com/photo-1782803432396-e168aa0e54a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMHJlc2lkZW50aWFsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4ODU2NTY4MHww&ixlib=rb-4.1.0&q=85",
  p2: "https://images.unsplash.com/photo-1760155312963-a50596435558?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMHJlc2lkZW50aWFsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4ODU2NTY4MHww&ixlib=rb-4.1.0&q=85",
  p3: "https://images.unsplash.com/photo-1758565811178-6341e9999f48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMHJlc2lkZW50aWFsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4ODU2NTY4MHww&ixlib=rb-4.1.0&q=85",
  n1: "https://images.unsplash.com/photo-1779241883587-21546986f21b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxuZWlnaGJvcmhvb2QlMjBsdXh1cnklMjBzdWJ1cmJhbnxlbnwwfHx8fDE3ODg1NjU2ODl8MA&ixlib=rb-4.1.0&q=85",
  n2: "https://images.unsplash.com/photo-1772693109381-620f0622646b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxuZWlnaGJvcmhvb2QlMjBsdXh1cnklMjBzdWJ1cmJhbnxlbnwwfHx8fDE3ODg1NjU2ODl8MA&ixlib=rb-4.1.0&q=85",
  n3: "https://images.unsplash.com/photo-1787317430225-9165bc506772?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxuZWlnaGJvcmhvb2QlMjBsdXh1cnklMjBzdWJ1cmJhbnxlbnwwfHx8fDE3ODg1NjU2ODB8MA&ixlib=rb-4.1.0&q=85",
};

const FOREST = "#1B4D3E";
const serif = { fontFamily: "'Playfair Display', serif" };
const script = { fontFamily: "'Alex Brush', cursive" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const PROPS = [
  { img: IMG.p1, tag: "New", price: "$1,295,000", beds: 4, baths: 3, sqft: "2,512", addr: "728 Oakridge Ave, Mapleton" },
  { img: IMG.p2, tag: "Open House", price: "$1,875,000", beds: 5, baths: 4, sqft: "3,420", addr: "1142 Hillview Dr, Riverside" },
  { img: IMG.p3, tag: null, price: "$998,000", beds: 5, baths: 2, sqft: "1,986", addr: "421 Elm St, Brookdale" },
];
const HOODS = [
  { img: IMG.n1, name: "Mapleton", vibe: "Historic charm. A strong sense of community." },
  { img: IMG.n2, name: "Riverside", vibe: "Trails, parks and a vibrant downtown." },
  { img: IMG.n3, name: "Brookdale", vibe: "Local shops. Great schools. A place to belong." },
];

const Cap = ({ children }) => <span className="text-xs tracking-widest uppercase font-semibold" style={{ color: FOREST }}>{children}</span>;

export default function MayaBennettDemo() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Maya Bennett — Local Real Estate (Demo) | WebsiteTech.pro"; }, []);

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", color: "#11181C", ...body }} data-testid="maya-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5" style={{ background: "#111827", color: "#F9FAFB" }}>
        WebsiteTech.pro Demo · Real Estate — click to view all industry demos
      </Link>

      {/* nav */}
      <DemoTopNav
        testid="maya-nav"
        sticky
        logo={<div className="leading-tight"><div className="text-lg tracking-[0.25em]" style={serif}>MAYA BENNETT</div><div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: FOREST }}>Local Real Estate</div></div>}
        items={["Properties", "Neighborhoods", "Sell", "About"]}
        ctaLabel="Contact"
        theme={{ bg: "rgba(250,250,247,0.92)", text: "#11181C", accent: FOREST, accentText: "#fff", border: `${FOREST}1f` }}
      />

      {/* hero */}
      <header data-testid="maya-hero" className="relative">
        <img src={IMG.n1} alt="Neighborhood" className="h-[460px] w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(17,24,28,0.6), rgba(17,24,28,0.15))" }} />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="max-w-xl text-white">
              <span className="text-xs tracking-[0.25em] uppercase">Local Insight · Exceptional Results</span>
              <h1 className="mt-4 text-5xl lg:text-6xl leading-[1.05]" style={serif}>Find a home<br />that <span style={{ ...script, fontSize: "1.1em" }}>feels like yours.</span></h1>
              <div data-testid="maya-search-bar" className="mt-8 flex items-center gap-2 bg-white rounded-full p-1.5 pl-5 shadow-xl max-w-lg">
                <Search size={18} className="text-slate-400" />
                <input placeholder="City, neighborhood, ZIP or address" className="flex-1 bg-transparent text-sm text-slate-800 outline-none py-2" />
                <button className="text-xs tracking-[0.15em] uppercase px-5 py-3 rounded-full text-white shrink-0" style={{ background: FOREST }}>Search Homes</button>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* featured properties */}
      <section data-testid="maya-featured-properties" className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div><Cap>Featured Properties</Cap><h2 className="mt-2 text-3xl md:text-4xl" style={serif}>Homes worth coming home to</h2></div>
          <button className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: FOREST }}>View all properties <ArrowRight size={15} /></button>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {PROPS.map((p, i) => (
            <motion.div key={p.addr} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}
              className="group bg-white rounded-2xl overflow-hidden border" style={{ borderColor: `${FOREST}1f`, boxShadow: "0 10px 30px -18px rgba(27,77,62,0.35)" }}>
              <div className="relative overflow-hidden">
                <img src={p.img} alt={p.addr} className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {p.tag && <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full text-white" style={{ background: FOREST }}>{p.tag}</span>}
                <button className="absolute top-3 right-3 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-slate-500"><Heart size={15} /></button>
              </div>
              <div className="p-5">
                <div className="text-2xl font-bold" style={serif}>{p.price}</div>
                <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1.5"><Bed size={15} /> {p.beds}</span>
                  <span className="flex items-center gap-1.5"><Bath size={15} /> {p.baths}</span>
                  <span className="flex items-center gap-1.5"><Maximize size={14} /> {p.sqft} sqft</span>
                </div>
                <div className="mt-3 text-sm text-slate-500 flex items-center gap-1.5"><MapPin size={14} style={{ color: FOREST }} /> {p.addr}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* neighborhoods */}
      <section data-testid="maya-neighborhoods" className="py-20 border-y" style={{ background: "#F3F3ED", borderColor: `${FOREST}14` }}>
        <div className="max-w-7xl mx-auto px-6">
          <Cap>Explore our Neighborhoods</Cap>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {HOODS.map((n) => (
              <div key={n.name} className="group bg-white rounded-2xl overflow-hidden border" style={{ borderColor: `${FOREST}1f` }}>
                <div className="overflow-hidden"><img src={n.img} alt={n.name} className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-5">
                  <h3 className="text-xl" style={serif}>{n.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-500">{n.vibe}</p>
                  <button className="mt-3 inline-flex items-center gap-1.5 text-xs tracking-widest uppercase font-semibold" style={{ color: FOREST }}>Learn More <ArrowRight size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* meet maya + home worth */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div data-testid="maya-bio" className="grid sm:grid-cols-5 gap-6 items-center">
          <img src={IMG.agent} alt="Maya Bennett" className="sm:col-span-2 rounded-2xl h-64 w-full object-cover" />
          <div className="sm:col-span-3">
            <Cap>Meet Maya</Cap>
            <h2 className="mt-2 text-3xl" style={serif}>Your local advocate in every move.</h2>
            <p className="mt-3 text-slate-600">Real estate is about more than transactions — it's about people, places and what's next. I'm proud to help buyers and sellers feel at home in the communities we love.</p>
            <button className="mt-5 text-xs tracking-widest uppercase px-5 py-3 rounded-full text-white" style={{ background: FOREST }}>Learn More About Maya</button>
          </div>
        </div>
        <div data-testid="maya-home-worth" className="rounded-3xl p-8 text-white relative overflow-hidden" style={{ background: FOREST }}>
          <span className="opacity-70 text-xs tracking-widest uppercase">Curious about your home's value?</span>
          <h3 className="mt-2 text-3xl" style={serif}>What is your home worth?</h3>
          <p className="mt-3 opacity-80 text-sm">Get a personalized home value report based on local market data and neighborhood expertise.</p>
          <div className="mt-6 flex items-center gap-2 bg-white rounded-full p-1.5 pl-5">
            <input placeholder="Enter your address" className="flex-1 bg-transparent text-sm text-slate-800 outline-none py-2" />
            <button className="text-xs tracking-widest uppercase px-5 py-2.5 rounded-full text-white shrink-0" style={{ background: "#0f2f26" }}>Get My Value</button>
          </div>
        </div>
      </section>

      {/* testimonials */}
      <section data-testid="maya-testimonials" className="py-16 border-t" style={{ background: "#F3F3ED", borderColor: `${FOREST}14` }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Cap>What clients are saying</Cap>
          <div className="mt-4 flex justify-center gap-1" style={{ color: "#C59B27" }}>{[0,1,2,3,4].map((i) => <Star key={i} size={18} fill="#C59B27" />)}</div>
          <p className="mt-5 text-2xl italic" style={serif}>"Maya made the entire process feel easy and personal. Her knowledge of the area is unmatched, and she truly cares about her clients."</p>
          <p className="mt-4 text-sm text-slate-500">— Taylor R., Mapleton</p>
        </div>
      </section>

      {/* footer */}
      <footer data-testid="maya-footer" className="py-14 text-white" style={{ background: "#0f2f26" }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-sm">
          <div><div className="text-lg tracking-[0.2em]" style={serif}>MAYA BENNETT</div><div className="text-[9px] tracking-[0.3em] uppercase opacity-70 mt-1">Local Real Estate</div><p className="mt-4 opacity-70">Stronger communities. Brighter tomorrows.</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Explore</div><p className="opacity-70 leading-7">Properties<br />Neighborhoods<br />Sell your home</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Contact</div><p className="opacity-70 leading-7">hello@mayabennett.demo<br />+1 (555) 018-4400</p></div>
          <div><div className="uppercase tracking-widest text-xs opacity-80 mb-3">Equal Housing Opportunity</div><p className="opacity-70">Licensed Real Estate Broker. All information deemed reliable but not guaranteed.</p></div>
        </div>
      </footer>

      <AIConciergeWidget demo="real-estate" concierge={DEMO_THEMES["real-estate"].concierge} />
    </div>
  );
}
